import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import EmptyState from "@/components/shared/EmptyState";
import RecipeForm from "@/components/recipes/RecipeForm";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, ChefHat, Search, Pencil, Clock, Copy, Sparkles, Trash2, Upload, BarChart2, LayoutDashboard, FileDown } from "lucide-react";
import MenuImportModal from "@/components/recipes/MenuImportModal";
import CsvImportModal from "@/components/recipes/CsvImportModal";
import RecipeProfitability from "@/components/recipes/RecipeProfitability";
import RecipeAIAdvisor from "@/components/recipes/RecipeAIAdvisor";
import CartaDashboard from "@/components/recipes/CartaDashboard";
import { resolveFileUrl } from "@/utils";

const CAT_LABELS = {
  starter: "Entrant",
  main: "Principal",
  dessert: "Postres",
  drink: "Beguda",
  side: "Guarnició",
  sauce: "Salsa",
  bread: "Pa",
  other: "Altres",
};

const CAT_COLORS = {
  starter: "bg-amber-100 text-amber-700",
  main: "bg-red-100 text-red-700",
  dessert: "bg-pink-100 text-pink-700",
  drink: "bg-blue-100 text-blue-700",
  side: "bg-green-100 text-green-700",
  sauce: "bg-orange-100 text-orange-700",
  bread: "bg-yellow-100 text-yellow-700",
  other: "bg-gray-100 text-gray-700",
};

function getMarginMeta(recipe) {
  if (!recipe.selling_price || !recipe.estimated_cost) return null;
  const pct = ((recipe.selling_price - recipe.estimated_cost) / recipe.selling_price) * 100;
  if (pct >= 65) return { pct, label: "Alt", class: "bg-emerald-100 text-emerald-700" };
  if (pct >= 45) return { pct, label: "Acceptable", class: "bg-amber-100 text-amber-700" };
  return { pct, label: "Baix", class: "bg-red-100 text-red-700" };
}

export default function RecipeSheets() {
  const { currentVenue, user, loading: venueLoading } = useVenue();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("catalog"); // "catalog" | "profitability" | "dashboard"
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [advisorRecipe, setAdvisorRecipe] = useState(null);

  useEffect(() => {
    if (currentVenue) loadRecipes();
  }, [currentVenue]);

  const loadRecipes = async () => {
    setLoading(true);
    const items = await hosys.entities.RecipeSheet.filter(
      { venue_id: currentVenue.id },
      "-created_date"
    );
    setRecipes(items);
    setLoading(false);
  };

  const handleSave = async (data) => {
    if (editingRecipe) {
      await hosys.entities.RecipeSheet.update(editingRecipe.id, data);
    } else {
      await hosys.entities.RecipeSheet.create(data);
    }
    setEditingRecipe(null);
    await loadRecipes();
  };

  const handleDuplicate = async (recipe) => {
    const { id, created_date, updated_date, created_by, ...rest } = recipe;
    await hosys.entities.RecipeSheet.create({
      ...rest,
      name: `${rest.name} (còpia)`,
    });
    await loadRecipes();
  };

  const handleDelete = async (recipe) => {
    {
      await hosys.entities.RecipeSheet.delete(recipe.id);
      await loadRecipes();
    }
  };

  if (venueLoading) return <QuickLoader />;

  const filtered = search
    ? recipes.filter(r =>
        r.name?.toLowerCase().includes(search.toLowerCase()) ||
        CAT_LABELS[r.category]?.toLowerCase().includes(search.toLowerCase())
      )
    : recipes;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fitxes Tècniques"
        subtitle={`${recipes.length} plats registrats`}
        actions={
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setShowImport(true)}
              className="gap-2 rounded-xl"
            >
              <Sparkles className="w-4 h-4 text-[#7B2D3F]" /> Importar carta
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowCsvImport(true)}
              className="gap-2 rounded-xl"
            >
              <Upload className="w-4 h-4 text-[#7B2D3F]" /> Importar CSV
            </Button>
            <Button
              onClick={() => { setEditingRecipe(null); setShowForm(true); }}
              className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2 rounded-xl"
            >
              <Plus className="w-4 h-4" /> Nou plat
            </Button>
          </div>
        }
      />

      {/* View tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        <button
          onClick={() => setView("catalog")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            view === "catalog" ? "bg-white shadow-sm text-gray-800" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <ChefHat className="w-4 h-4" /> Catàleg
        </button>
        <button
          onClick={() => setView("profitability")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            view === "profitability" ? "bg-white shadow-sm text-gray-800" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <BarChart2 className="w-4 h-4" /> Anàlisi
        </button>
        <button
          onClick={() => setView("dashboard")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            view === "dashboard" ? "bg-white shadow-sm text-gray-800" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <LayoutDashboard className="w-4 h-4" /> Visió Global
        </button>
      </div>

      {/* Catalog view */}
      {view === "catalog" && (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Cercar per nom o categoria..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          {loading ? (
            <QuickLoader />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={ChefHat}
              title="Sense fitxes tècniques"
              description="Crea la primera fitxa tècnica d'un plat."
              action={
                <Button onClick={() => setShowForm(true)} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2">
                  <Plus className="w-4 h-4" /> Crear fitxa
                </Button>
              }
            />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((recipe) => {
                const mm = getMarginMeta(recipe);
                return (
                  <Card key={recipe.id} className="border-0 shadow-sm hover:shadow-md transition-all overflow-hidden">
                    {recipe.photo_url && (
                      <div className="h-40 bg-gray-100">
                        <img src={resolveFileUrl(recipe.photo_url)} alt={recipe.name} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-semibold text-gray-900 text-base leading-tight">{recipe.name}</h3>
                        <Badge variant="secondary" className={`${CAT_COLORS[recipe.category]} text-xs border-0 flex-shrink-0`}>
                          {CAT_LABELS[recipe.category]}
                        </Badge>
                      </div>
                      {recipe.description && (
                        <p className="text-sm text-gray-500 line-clamp-2 mb-3">{recipe.description}</p>
                      )}
                      {recipe.allergens?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {recipe.allergens.map(a => (
                            <span key={a} className="text-[10px] bg-red-50 text-red-700 px-1.5 py-0.5 rounded-full font-medium">{a}</span>
                          ))}
                        </div>
                      )}

                      {/* Cost & margin row */}
                      <div className="mb-3">
                       <div className="flex items-center gap-2 flex-wrap mb-2">
                         {recipe.preparation_time > 0 && (
                           <span className="flex items-center gap-1 text-xs text-gray-500">
                             <Clock className="w-3 h-3" /> {recipe.preparation_time}min
                           </span>
                         )}
                         {recipe.estimated_cost > 0 && (
                           <span className="text-xs text-gray-500">Cost: <strong>{recipe.estimated_cost.toFixed(2)}€</strong></span>
                         )}
                         {recipe.selling_price > 0 && (
                           <span className="text-xs font-semibold text-gray-700">PVP: {recipe.selling_price.toFixed(2)}€</span>
                         )}
                       </div>
                       {mm && (
                         <div className="space-y-1">
                           <div className="flex items-center justify-between">
                             <span className={`text-[10px] font-bold ${mm.class.split(" ")[1]}`}>Marge: {mm.pct.toFixed(1)}%</span>
                             <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${mm.class}`}>{mm.label}</span>
                           </div>
                           <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                             <div
                               className={`h-full rounded-full transition-all ${mm.pct >= 65 ? "bg-emerald-500" : mm.pct >= 45 ? "bg-amber-400" : "bg-red-400"}`}
                               style={{ width: `${Math.min(Math.max(mm.pct, 0), 100)}%` }}
                             />
                           </div>
                         </div>
                       )}
                      </div>

                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="flex-1 gap-1"
                          onClick={() => { setEditingRecipe(recipe); setShowForm(true); }}>
                          <Pencil className="w-3 h-3" /> Editar
                        </Button>
                        {recipe.selling_price > 0 && recipe.estimated_cost > 0 && (
                          <Button variant="outline" size="sm" className="gap-1 text-[#7B2D3F]"
                            onClick={() => setAdvisorRecipe(recipe)}>
                            <Sparkles className="w-3 h-3" />
                          </Button>
                        )}
                        <Button variant="outline" size="sm" className="gap-1 text-[#7B2D3F]"
                          data-testid={`recipe-pdf-btn-${recipe.id}`}
                          onClick={() => hosys.recipePdf.download(recipe.id, recipe.name)}>
                          <FileDown className="w-3 h-3" />
                        </Button>
                        <Button variant="outline" size="sm" className="gap-1"
                          onClick={() => handleDuplicate(recipe)}>
                          <Copy className="w-3 h-3" />
                        </Button>
                        <Button variant="outline" size="sm" className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(recipe)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Profitability view */}
      {view === "profitability" && (
        loading ? <QuickLoader /> : <RecipeProfitability recipes={recipes} />
      )}

      {/* Dashboard / global view */}
      {view === "dashboard" && (
        loading ? <QuickLoader /> : <CartaDashboard recipes={recipes} />
      )}

      {advisorRecipe && (
        <RecipeAIAdvisor recipe={advisorRecipe} onClose={() => setAdvisorRecipe(null)} />
      )}

      <MenuImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        venueId={currentVenue?.id}
        onImported={async () => { setShowImport(false); await loadRecipes(); }}
      />

      <CsvImportModal
        open={showCsvImport}
        onClose={() => setShowCsvImport(false)}
        venueId={currentVenue?.id}
        onImported={async () => { setShowCsvImport(false); await loadRecipes(); }}
      />

      <RecipeForm
        open={showForm}
        onClose={() => { setShowForm(false); setEditingRecipe(null); }}
        onSave={handleSave}
        venueId={currentVenue?.id}
        editData={editingRecipe}
      />
    </div>
  );
}